import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { GENERATE_SIGN_UPLOAD_FILE } from '../services/files';
import { showNotification } from '@mantine/notifications';
import { ActionIcon, Box, Button, Center, Group, Modal, RingProgress, Stack, Text } from '@mantine/core';
import { FaPause, FaPlay } from 'react-icons/fa6';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { formatMilliseconds } from '../utility/util';

const VideoRecorder = ({ value, onUploaded, max = 120 }: { max?: number; value?: string; onUploaded: (url: string) => void }) => {
    const videoRef = useRef<any>(null);
    const previewRef = useRef<any>(null);
    const mediaRecorderRef = useRef<any>(null);
    const recordedChunks = useRef<any[]>([]);
    const durationInterval = useRef<any>(null);

    const [previewUrl, setPreviewUrl] = useState<any>(null);
    const [isRecording, setIsRecording] = useState(false);
    const [serverProgress, setServerProgress] = useState(0);
    const [awsProgress, setAwsProgress] = useState(0);
    const [error, setError] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [loadingUpload, setLoadingUpload] = useState(false);
    const [stream, setStream] = useState<any>(null);
    const [duration, setDuration] = useState<number>(0);

    const startRecording = async () => {
        try {
            setIsRecording(true);
            setDuration(0);
            previewRef.current.pause()

            recordedChunks.current = [];
            mediaRecorderRef.current = new MediaRecorder(stream);
            mediaRecorderRef.current.ondataavailable = (event: any) => {
                if (event.data.size > 0) {
                    recordedChunks.current.push(event.data);
                }
            };
            mediaRecorderRef.current.onstop = (e: any) => {
                clearInterval(durationInterval.current)
                const blob = new Blob(recordedChunks.current, { type: 'video/webm' });
                const previewUrl = URL.createObjectURL(blob);
                setPreviewUrl(previewUrl);
                setIsRecording(false);
                setIsPlaying(false);
            }
            mediaRecorderRef.current.start()
            durationInterval.current = setInterval(() => { setDuration(d => d += 300) }, 300);
        } catch (error) {
            console.error('Error accessing media devices:', error);
        }
    };

    const stopRecording = async () => {
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
        }

        if (videoRef.current && videoRef.current.srcObject) {
            const tracks = videoRef.current.srcObject.getTracks();
            tracks.forEach((track: any) => track.stop());
        }
    };

    const tooglePreview = () => {
        if (!loadingUpload) {
            previewRef.current.muted = false;
            if (isPlaying) previewRef.current.pause();
            else previewRef.current.play();
        }
    }

    const uploadVideo = (file: any, params: any) => {
        setLoadingUpload(true);
        GENERATE_SIGN_UPLOAD_FILE(params)
            .then((res) => {
                axios
                    .put(res.signedUrl, file, {
                        headers: { 'Content-Type': file.type },
                        onUploadProgress: (progressEvent) => {
                            const totalLength = progressEvent.lengthComputable
                                ? progressEvent.total
                                : progressEvent.target.getResponseHeader('content-length') ||
                                progressEvent.target.getResponseHeader('x-decompressed-content-length');
                            if (totalLength !== null) {
                                setServerProgress(Math.round((progressEvent.loaded * 100) / totalLength));
                                setAwsProgress(Math.round((progressEvent.loaded * 100) / totalLength));
                            }
                        },
                    })
                    .then(() => {
                        setOpenModal(false);
                        setLoadingUpload(false);
                        onUploaded(res.generatedUrl);
                        showNotification({ message: 'Video uploaded successfully!', color: 'green' });
                    })
                    .catch((err) => {
                        setLoadingUpload(false);
                        setError(err.message);
                        showNotification({ message: err.message, color: 'red' });
                    });
            })
            .catch((err) => {
                setLoadingUpload(false);
                setError(err.message);
                showNotification({ title: 'Failed to upload video', message: err.message, color: 'red' });
            });
    };

    const handleUpload = () => {
        const blob = new Blob(recordedChunks.current, { type: 'video/webm' });
        const file = new File([blob], 'recording.webm', { type: 'video/webm' });
        const params = {
            size: file.size,
            filename: file.name,
            contentType: file.type,
            mimetype: file.type,
        };
        uploadVideo(file, params);
    };

    useEffect(() => {
        (async () => {
            if(openModal && !previewUrl){
                const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play();
                }
                setStream(stream);
            }else{
                stopRecording();
                setStream(null)
            }
        })()
    }, [openModal, previewUrl])
    
    useEffect(() => {
        if(!previewUrl && previewRef.current) previewRef.current.pause();
    }, [previewUrl])

    return (
        <>
            {
                <Modal
                    opened={openModal}
                    onClose={() => setOpenModal(false)}
                    withCloseButton={false}
                    size="90vw"
                    styles={{
                        body: { padding: 0, height: "calc(90vh)" }
                    }}
                >
                    <video style={{ width: "100%", height: "calc(90vh)", objectFit: "cover", background: 'black', display: (isRecording || !previewUrl) ? 'block' : 'none' }} ref={videoRef} autoPlay muted />
                    <video
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        style={{ width: "100%", height: "calc(90vh)", cursor: "pointer", objectFit: "cover", background: 'black', display: (!isRecording && previewUrl) ? 'block' : 'none' }}
                        ref={previewRef}
                        src={previewUrl}
                        onClick={tooglePreview}
                    />
                    <ActionIcon
                        variant='light'
                        color="gray"
                        onClick={() => setOpenModal(false)}
                        style={{ position: 'absolute', top: 20, right: 20 }}>
                        <FaTimes />
                    </ActionIcon>
                    {
                        previewUrl && !isPlaying && <ActionIcon
                            variant='light'
                            size="100px"
                            onClick={tooglePreview}
                            color='white'
                            style={{ position: 'absolute', top: "calc(50% - 50px)", left: "calc(50% - 50px)" }}>
                            {isPlaying ? <FaPause size={60} /> : <FaPlay size={60} />}
                        </ActionIcon>
                    }
                    <Group justify='center' style={{ position: 'absolute', bottom: 20, left: 20, right: 20 }}>
                        {
                            isRecording
                                ? <div style={{ position: 'relative' }}>
                                    <Text ta="center" style={{
                                        position: 'absolute',
                                        top: -24,
                                        left: 0,
                                        right: 0,
                                    }}>{formatMilliseconds(duration)}</Text>
                                    <RingProgress
                                        styles={{
                                            root: { background: '#00000088', borderRadius: 200 },
                                            curve: { background: 'red' }
                                        }}
                                        label={
                                            <Center>
                                                <ActionIcon variant="light" size="xl" onClick={stopRecording}>
                                                    <div style={{ width: 40, height: 40, borderRadius: 3, background: "#fe4656", display: 'flex', flexDirection: 'row' }}>
                                                    </div>
                                                </ActionIcon>
                                            </Center>
                                        }
                                        sections={[
                                            { value: 100 * (duration / (max * 1000)), color: 'red' },
                                            { value: 100 * (((max * 1000) - duration) / (max * 1000)), color: 'gray' },
                                        ]}
                                    />
                                </div>
                                : <>
                                    {
                                        previewUrl
                                            ? <Stack>
                                                <Button leftSection={<FaCheck />} color="gray" variant='filled' onClick={handleUpload} loading={loadingUpload}>Continue</Button>
                                                <Button leftSection={<FaTimes />} color="gray" variant='light' onClick={() => {
                                                    setPreviewUrl(null);
                                                }} loading={loadingUpload}>Retry</Button>
                                            </Stack>
                                            : <>
                                                <div
                                                    onClick={startRecording}
                                                    style={{ cursor: "pointer", width: 100, height: 100, borderRadius: 50, background: "#efefef", display: 'flex', alignItems: "center", justifyContent: "center" }}>
                                                    <div style={{ width: 92, height: 92, borderRadius: 46, background: "#fe4656" }}>

                                                    </div>
                                                </div>
                                            </>
                                    }
                                </>
                        }
                    </Group>
                </Modal>
            }
            <Stack justify='center'>
                {
                    value && <video
                        style={{ width: "100%", height: "350px", background: 'black', objectFit: "cover" }}
                        controls
                        src={value}
                    />
                }
                <Button leftSection={<FaPlay />} size="xl" variant="light" onClick={() => setOpenModal(true)}>Start Recording</Button>
            </Stack>
        </>
    );
};

export default VideoRecorder;
